import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, Loading, EmptyState } from "@/components/common/Primitives";
import { KpiCard } from "@/components/common/KpiCard";
import { fmtEUR, fmtDateTime } from "@/lib/format";
import { Boxes, Smartphone, PackageCheck, AlertTriangle, Lock, Wrench, Euro, Layers, Inbox } from "lucide-react";

export default function WawiDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["wawi-dash"], queryFn: async () => (await api.get("/wawi/dashboard")).data });
  if (isLoading) return <Loading />;
  const k = data?.kpis || {};
  const empty = (k.articles || 0) === 0 && (k.devices || 0) === 0;

  return (
    <div>
      <PageHeader title="Warenwirtschaft" description="Bestände, Geräte und Lagerbewegungen in Echtzeit" />
      {empty ? (
        <EmptyState icon={Inbox} title="Noch keine Warenwirtschaftsdaten"
          description="Legen Sie Artikel an und erfassen Sie Geräte, um Bestände und Kennzahlen zu sehen."
          action={<button onClick={() => navigate("/wawi/artikel")} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground">Artikel anlegen</button>} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard testId="wawi-kpi-articles" label="Artikel" value={k.articles} icon={Boxes} onClick={() => navigate("/wawi/artikel")} />
            <KpiCard testId="wawi-kpi-variants" label="Varianten" value={k.variants} icon={Layers} />
            <KpiCard testId="wawi-kpi-devices" label="Geräte gesamt" value={k.devices} icon={Smartphone} onClick={() => navigate("/wawi/geraete")} />
            <KpiCard testId="wawi-kpi-available" label="Verfügbar" value={k.available} icon={PackageCheck} tone="accent" />
            <KpiCard testId="wawi-kpi-blocked" label="Gesperrt" value={k.blocked} icon={Lock} tone="danger" />
            <KpiCard testId="wawi-kpi-check" label="In Prüfung" value={k.in_check} icon={AlertTriangle} tone="warning" />
            <KpiCard testId="wawi-kpi-repair" label="In Reparatur" value={k.in_repair} icon={Wrench} tone="info" />
            <KpiCard testId="wawi-kpi-value" label="Lagerwert (EK)" value={fmtEUR(k.stock_value)} icon={Euro} />
          </div>
          {k.low_stock > 0 && (
            <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4" />{k.low_stock} Artikel unter Mindestbestand
            </div>
          )}
          <SectionCard title="Letzte Lagerbewegungen">
            <div className="space-y-2">
              {(data?.recent_movements || []).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Keine Bewegungen.</p>}
              {(data?.recent_movements || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                  <span className="font-medium capitalize">{m.type}</span>
                  <span className="text-xs text-muted-foreground">{m.user_name} · {fmtDateTime(m.at)}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
